import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { submissionAPI } from '../services/api';
import { FormattedText } from '../utils/formatText';
import CodeDragDropResults from '../components/CodeDragDropResults';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useToast } from '../components/Toast';
import ReportIssueButton from '../components/ReportIssueButton';

function QuizResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [result] = useState(location.state?.result || null);
  const [quiz] = useState(location.state?.quiz || null);

  useEffect(() => {
    // Redirect if no result data
    if (!result || !quiz) {
      navigate('/');
      return;
    }

    // Ensure detailedResults exists
    if (!result.detailedResults) {
      console.error('Missing detailedResults in result:', result);
      result.detailedResults = [];
    }
  }, [result, quiz, navigate]);

  if (!result || !quiz) {
    return null;
  }

  const passed = result.percentage >= (quiz.passingPercentage || 40);

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '900px', margin: '0 auto' }}>
        {result.attemptNumber > 1 && (
          <div style={{
            marginBottom: '24px',
            padding: '16px 20px',
            backgroundColor: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
              🔄 PRACTICE MODE - Practice Attempt #{result.attemptNumber - 1}
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#78350f' }}>
              This score is for practice only and won't affect your ranking. Your first attempt counts for the leaderboard.
            </p>
          </div>
        )}
        
        <h2 style={{ color: '#667eea', marginBottom: '24px', textAlign: 'center', fontSize: '26px' }}>
          🎉 Quiz Completed!
        </h2>
        
        <div style={{ 
          padding: '32px', 
          backgroundColor: passed ? '#c6f6d5' : '#fed7d7',
          borderRadius: '12px',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          <h1 style={{ 
            fontSize: '48px', 
            margin: '0',
            color: passed ? '#2f855a' : '#c53030'
          }}>
            {result.percentage}%
          </h1>
          <p style={{ 
            fontSize: '18px', 
            marginTop: '8px',
            color: passed ? '#2f855a' : '#c53030',
            fontWeight: '600'
          }}>
            {passed ? '🎉 Congratulations!' : '💪 Keep Learning!'}
          </p>
        </div>

        <div style={{ marginBottom: '24px', fontSize: '14px', textAlign: 'center', color: '#4a5568' }}>
          <p>
            You answered <strong style={{ color: '#667eea' }}>{result.score}</strong> out of{' '}
            <strong style={{ color: '#667eea' }}>{result.totalQuestions}</strong> questions correctly
          </p>
        </div>

        {/* Performance Stats */}
        {result.percentile !== undefined && (
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#f0f4ff',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#4a5568' }}>
              You scored better than <strong style={{ color: '#667eea', fontSize: '16px' }}>{result.percentile}%</strong> of participants
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#718096' }}>
              Total participants: {result.totalParticipants}
            </p>
          </div>
        )}

        {/* Detailed Results */}
        {result.detailedResults && result.detailedResults.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              marginBottom: '20px', 
              color: '#2d3748', 
              fontSize: '20px', 
              fontWeight: '700',
              borderBottom: '3px solid #667eea',
              paddingBottom: '12px'
            }}>
              🔍 Review Your Answers
            </h3>
            {result.detailedResults && result.detailedResults.map((item, index) => {
              if (!item || !item.question) {
                console.error('Invalid item in detailedResults:', item, 'at index:', index);
                return null;
              }
              
              const question = item.question;
              
              return (
                <div 
                  key={item.questionId} 
                  style={{ 
                    marginBottom: '20px',
                    padding: '0',
                    border: 'none',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ 
                    padding: '20px',
                    backgroundColor: item.isCorrect ? '#f0fdf4' : '#fef2f2',
                    borderLeft: `6px solid ${item.isCorrect ? '#22c55e' : '#ef4444'}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ 
                          fontSize: '11px', 
                          fontWeight: '700', 
                          color: item.isCorrect ? '#15803d' : '#b91c1c',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Question {index + 1} {item.isCorrect ? '✓ CORRECT' : '✗ INCORRECT'}
                        </span>
                      </div>
                      <ReportIssueButton
                        sourceType="quiz"
                        sourceId={quiz._id}
                        questionPath={`questions.${index}`}
                        questionId={question._id}
                        questionIndex={index}
                        questionData={{
                          question: question.question,
                          options: question.options,
                          correctAnswer: question.correctAnswer,
                          questionType: question.questionType,
                          explanation: question.explanation,
                          difficulty: question.difficulty,
                          tags: question.tags,
                          category: question.category
                        }}
                        metadata={{
                          sourceName: quiz.title,
                          sourceCode: quiz.code,
                          questionNumber: index + 1,
                          totalQuestions: quiz.questions?.length || 0,
                          userAnswer: item.selectedAnswer,
                          isCorrect: item.isCorrect,
                          attemptNumber: result.attemptNumber,
                          submissionId: result.submissionId,
                          reportedFrom: 'quiz-results',
                          timestamp: new Date().toISOString(),
                          groupName: quiz.group?.name,
                          groupId: quiz.group?._id
                        }}
                        size="icon-only"
                      />
                    </div>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <FormattedText text={question.question} style={{ 
                        fontSize: '15px', 
                        fontWeight: '600',
                        color: '#1a202c',
                        lineHeight: '1.6'
                      }} />
                    </div>

                    {/* Code Reference */}
                    {question.codeReference && (
                      <div style={{ marginBottom: '16px' }}>
                        <SyntaxHighlighter
                          language={question.codeLanguage || 'javascript'}
                          style={vscDarkPlus}
                          customStyle={{
                            borderRadius: '8px',
                            fontSize: '13px',
                            padding: '16px',
                            margin: 0
                          }}
                        >
                          {question.codeReference}
                        </SyntaxHighlighter>
                      </div>
                    )}

                    {/* Code Drag & Drop Question Results */}
                    {question.questionType === 'CodeDragDrop' && item.selectedAnswer ? (
                      <CodeDragDropResults
                        correctCode={question.correctAnswer}
                        userAnswer={item.selectedAnswer}
                        isCorrect={item.isCorrect}
                      />
                    ) : (
                      <>
                        {/* Regular Question Options */}
                        {question.questionType !== 'OpenEnded' && question.options && Array.isArray(question.options) && (
                          <div style={{ marginTop: '12px' }}>
                            {(() => {
                              // Use shuffled options if available, otherwise use original order
                              const shuffledOptionsForQuestion = result.shuffledOptions?.[question._id];
                              const displayOptions = shuffledOptionsForQuestion || 
                                question.options.map((option, index) => ({ option, originalIndex: index }));
                              
                              return displayOptions.map((optionItem, displayIdx) => {
                                const option = typeof optionItem === 'string' ? optionItem : optionItem.option;
                                const optIdx = typeof optionItem === 'string' ? displayIdx : optionItem.originalIndex;
                                const isUserAnswer = optIdx === item.selectedAnswer;
                                const isCorrectAnswer = optIdx === item.correctAnswer;
                              
                              let backgroundColor = '#ffffff';
                              let borderColor = '#e2e8f0';
                              let textColor = '#2d3748';
                              let icon = '';
                              let label = '';
                              let fontWeight = 'normal';
                              let boxShadow = 'none';
                              
                              if (isUserAnswer && isCorrectAnswer) {
                                backgroundColor = '#d1fae5';
                                borderColor = '#22c55e';
                                textColor = '#065f46';
                                icon = '✓';
                                label = 'Your Answer (Correct)';
                                fontWeight = '600';
                                boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.2)';
                              } else if (isUserAnswer && !isCorrectAnswer) {
                                backgroundColor = '#fee2e2';
                                borderColor = '#ef4444';
                                textColor = '#991b1b';
                                icon = '✗';
                                label = 'Your Answer';
                                fontWeight = '600';
                                boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.2)';
                              } else if (isCorrectAnswer) {
                                backgroundColor = '#d1fae5';
                                borderColor = '#22c55e';
                                textColor = '#065f46';
                                icon = '✓';
                                label = 'Correct Answer';
                              }
                              
                              return (
                                <div
                                  key={optIdx}
                                  style={{
                                    padding: '12px 16px',
                                    marginBottom: '8px',
                                    border: `2px solid ${borderColor}`,
                                    borderRadius: '8px',
                                    backgroundColor,
                                    color: textColor,
                                    fontSize: '14px',
                                    fontWeight,
                                    boxShadow,
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                  }}>
                                    {icon && (
                                      <span style={{ 
                                        fontSize: '16px', 
                                        fontWeight: 'bold',
                                        minWidth: '20px'
                                      }}>
                                        {icon}
                                      </span>
                                    )}
                                    <div style={{ flex: 1 }}>
                                      {label && (
                                        <div style={{ 
                                          fontSize: '11px', 
                                          fontWeight: '700',
                                          textTransform: 'uppercase',
                                          marginBottom: '4px',
                                          opacity: 0.9
                                        }}>
                                          {label}
                                        </div>
                                      )}
                                      <FormattedText text={option} />
                                    </div>
                                  </div>
                                </div>
                              );
                            });
                            })()}
                          </div>
                        )}

                        {/* Open Ended Answer */}
                        {question.questionType === 'OpenEnded' && (
                          <div style={{ marginTop: '12px' }}>
                            <div style={{
                              padding: '16px',
                              backgroundColor: '#f0f9ff',
                              borderRadius: '8px',
                              border: '2px solid #3b82f6',
                              boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                            }}>
                              <strong style={{ 
                                fontSize: '12px', 
                                color: '#1e40af', 
                                textTransform: 'uppercase',
                                fontWeight: '700'
                              }}>
                                📝 Your Answer:
                              </strong>
                              <p style={{ 
                                margin: '8px 0 0 0', 
                                color: '#1e3a8a',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                fontWeight: '500'
                              }}>
                                {item.selectedAnswer || 'No answer provided'}
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Explanation */}
                    {question.explanation && (
                      <div style={{
                        marginTop: '16px',
                        padding: '12px 16px',
                        backgroundColor: '#eff6ff',
                        borderLeft: '4px solid #3b82f6',
                        borderRadius: '4px'
                      }}>
                        <strong style={{ fontSize: '12px', color: '#1e40af', textTransform: 'uppercase' }}>
                          💡 Explanation:
                        </strong>
                        <FormattedText text={question.explanation} style={{ 
                          marginTop: '8px', 
                          color: '#1e3a8a',
                          fontSize: '13px',
                          lineHeight: '1.6'
                        }} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'center',
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '2px solid #e2e8f0'
        }}>
          <button
            onClick={() => navigate('/')}
            className="btn btn-primary"
            style={{ padding: '12px 32px' }}
          >
            🏠 Back to Dashboard
          </button>
          {quiz.showLeaderboard && (
            <button
              onClick={() => navigate('/leaderboard')}
              className="btn btn-secondary"
              style={{ padding: '12px 32px' }}
            >
              📊 View Leaderboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuizResults;
